import { Component, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { WorkflowNoticeService } from '../../core/workflow-notice.service';
import { IconComponent } from '../icon/icon.component';

/** docs/design/16_workflow_engine.md §14.3 (Phase 3b) — shows the admin-authored Validation failure message(s)
 * for the user's own last action, in the viewer's language. Sits next to the connectivity banner in the app
 * shell; a warning, not an error - the request itself succeeded, the workflow simply routed down its Failed
 * branch. */
@Component({
  selector: 'app-workflow-notice-banner',
  standalone: true,
  imports: [TranslatePipe, IconComponent],
  template: `
    @if (notice.errorMessage(); as errorText) {
      <div class="notice" role="alert" data-testid="workflow-write-error">
        <app-icon name="x" [size]="16" />
        <div class="notice__body">
          <strong>{{ 'shared.workflowNotice.errorTitle' | translate }}</strong>
          <p class="notice__message">{{ errorText }}</p>
        </div>
        <button type="button" (click)="notice.dismiss()">{{ 'shared.dismiss' | translate }}</button>
      </div>
    }
    @if (notice.notices().length > 0) {
      <div class="notice" role="alert" data-testid="workflow-validation-notice">
        <app-icon name="x" [size]="16" />
        <div class="notice__body">
          <strong>{{ 'shared.workflowNotice.title' | translate }}</strong>
          @for (item of notice.notices(); track $index) {
            <p class="notice__message">{{ messageFor(item) }}</p>
          }
        </div>
        <button type="button" (click)="notice.dismiss()">{{ 'shared.dismiss' | translate }}</button>
      </div>
    }
  `,
  styles: [
    `
      .notice {
        display: flex;
        align-items: flex-start;
        gap: var(--space-12);
        padding: 10px 20px;
        background: var(--warning-soft);
        color: var(--ink);
        border-bottom: 1px solid var(--warning);
        font-family: var(--font-display);
        font-size: 0.85rem;
        position: sticky;
        top: 0;
        z-index: var(--z-toast);
      }

      .notice__body {
        flex: 1;
      }

      .notice__message {
        margin: 4px 0 0;
      }

      button {
        flex: none;
        border: 0;
        background: transparent;
        color: inherit;
        opacity: 0.8;
        cursor: pointer;
        font-family: var(--font-display);
        font-size: 0.8rem;
      }
    `,
  ],
})
export class WorkflowNoticeBannerComponent {
  readonly notice = inject(WorkflowNoticeService);
  private readonly translate = inject(TranslateService);

  messageFor(item: { messageEn: string; messageAr: string }): string {
    return this.translate.currentLang() === 'ar' ? item.messageAr : item.messageEn;
  }
}
